import { c as _c } from "react/compiler-runtime";
import { memo } from 'react';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { useTracker } from '@openclassrooms/data-connect-web/tracking-events';
import { translate, getActiveLang } from '@openclassrooms/translations';
import Hyperlink from '@classify/react-core-components/Hyperlink';
import Feedback from '@mui/icons-material/Feedback';
import { RichContent } from '@openclassrooms/rich-content';
import { styled } from '@classify/react-core-components/styles';
import Stack from '@classify/react-core-components/Stack';
import Skeleton from '@classify/react-core-components/Skeleton';
import { useChapterContext } from '../_contexts/ChapterContext';
import BannerWrapper from './BannerWrapper';
import BottomBanner from './BottomBanner/BottomBanner';
interface Props {
  courseId: number;
  chapterId: number;
}
const InformationalBannerWrapper = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  margin: spacing(2, 0, 0),
  [breakpoints.up('md')]: {
    margin: spacing(4, 0, 0)
  }
}));
const ContentSkeleton = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton variant="rectangular" height={350} sx={{
      mt: 2
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <>{t0}<Skeleton variant="text" sx={{
        my: 2
      }} width={175} /><Skeleton variant="rectangular" height={150} /><Skeleton variant="rectangular" height={100} /></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const ChapterContentWrapper = memo(t0 => {
  const $ = _c(25);
  const {
    courseId,
    chapterId
  } = t0;
  const {
    userId
  } = useCurrentUserContext();
  const {
    chapterContent,
    currentChapter,
    course
  } = useChapterContext();
  const track = useTracker();
  const activeLang = getActiveLang() || "en";
  if (!chapterContent || !currentChapter) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ContentSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const {
    title: currentChapterTitle
  } = currentChapter;
  const t1 = `https://openclassrooms.tfaforms.net/4984649?tfa_19=${userId ?? ""}&tfa_13=${courseId}&tfa_15=${chapterId}&tfa_17=${course.categoryId}`;
  const t2 = `https://openclassrooms.tfaforms.net/4984703?tfa_19=${userId ?? ""}&tfa_13=${courseId}&tfa_15=${chapterId}&tfa_17=${course.categoryId}`;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      fr: t1,
      en: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const feedbackLink = t3[activeLang];
  const t4 = !userId;
  let t5;
  if ($[4] !== currentChapterTitle) {
    t5 = translate("courses.accessibility_labels.chapter.video_title", {
      chapterTitle: currentChapterTitle
    });
    $[4] = currentChapterTitle;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== chapterContent || $[7] !== t4 || $[8] !== t5) {
    t6 = <RichContent content={chapterContent} shouldZoomIntoImage={true} shouldLockMediaContent={t4} accessibleVideoTitle={t5} />;
    $[6] = chapterContent;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      mt: 2
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Feedback sx={{
      marginRight: 2,
      color: "brand.main"
    }} />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== chapterId || $[13] !== courseId || $[14] !== track || $[15] !== userId) {
    t9 = () => {
      track("Clicked Suggestion Link From Course", {
        data: {
          ...(userId ? {
            userId
          } : {}),
          courseId,
          chapterId
        }
      });
    };
    $[12] = chapterId;
    $[13] = courseId;
    $[14] = track;
    $[15] = userId;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = translate("chapterpage.feedback.button");
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== feedbackLink || $[19] !== t9) {
    t11 = <Stack alignItems="center" sx={t7} direction="row">{t8}<Hyperlink target="_blank" href={feedbackLink} onClick={t9}>{t10}</Hyperlink></Stack>;
    $[18] = feedbackLink;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <BannerWrapper><InformationalBannerWrapper><BottomBanner ctaDataEvent="Navigation|Redirection::Internal|Chapter Apply" /></InformationalBannerWrapper></BannerWrapper>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t6) {
    t13 = <>{t6}{t11}{t12}</>;
    $[22] = t11;
    $[23] = t6;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
});
export default ChapterContentWrapper;